/**
 * Shared by the run Downloads modal and the dataset download cart (aws-2-3).
 *
 * direct: a presigned GCS URL, used as is. stream: an S3 file Foundry Connect streams, a server
 * relative path that needs the session cookie (browser) or a personal access token (script).
 * proxy: an SSH file, browser only, fetched whole.
 *
 * File names and keys are data chosen by whoever wrote the object, so every value placed in a
 * script is single quoted (nothing expands inside single quotes) and every path is checked first.
 */
type Link = { url: string; method: "direct" | "proxy" | "stream" };

export const browserHref = (link: Link, basename: string): string =>
  link.method === "stream"
    ? `${basename.replace(/\/$/, "")}${link.url}`
    : link.url;

export const isNavigableDownload = (link: Link): boolean =>
  link.method === "direct" || link.method === "stream";

export const isScriptable = (link: Link): boolean =>
  link.method === "direct" || link.method === "stream";

export const needsStreamAuth = (links: Link[]): boolean =>
  links.some(link => link.method === "stream");

const absolute = (link: Link, origin: string, basename: string): string =>
  new URL(browserHref(link, basename), origin).toString();

export const NO_SCRIPTABLE_LINKS =
  "# No scriptable links.\n# Cluster files are downloaded through the browser.";

export const STREAM_AUTH_NOTE_UNIX =
  "# Files streamed by Foundry Connect need your personal access token (Profile, Personal Access Tokens):\n#   export FOUNDRY_TOKEN=<your personal access token>\n\n";

export const STREAM_AUTH_NOTE_WINDOWS =
  '# Files streamed by Foundry Connect need your personal access token (Profile, Personal Access Tokens):\n#   $env:FOUNDRY_TOKEN = "<your personal access token>"\n\n';

const CONTROL_CHARS = new RegExp(
  // eslint-disable-next-line no-control-regex
  "[\\u0000-\\u001f\\u007f-\\u009f\\u2028\\u2029]",
  "g"
);
const PS_QUOTES = new RegExp("['\\u2018\\u2019\\u201a\\u201b]", "g");

/** POSIX shell single quote: nothing expands, an embedded quote becomes '\''. */
export const shQuote = (value: string): string =>
  `'${value.replace(CONTROL_CHARS, " ").replace(/'/g, "'\\''")}'`;

/** PowerShell single quote: nothing expands; PowerShell also treats curly single quotes as quotes, so double them all. */
export const psQuote = (value: string): string =>
  `'${value.replace(CONTROL_CHARS, " ").replace(PS_QUOTES, ch => ch + ch)}'`;

/** Text for a `# File n: ...` comment line: no line break can end the comment. */
export const commentText = (value: string): string =>
  value.replace(CONTROL_CHARS, " ");

/** Empty, or only dots and spaces: Windows normalises such a name, so it can mean a parent folder. */
const isDotsAndSpaces = (s: string): boolean => /^[. ]*$/.test(s);

const bareName = (fileName: string): string => {
  const last = fileName.split(/[\\/]/).pop() ?? "";
  // ":" is replaced so the fallback can never be drive relative or an NTFS stream name.
  const clean = last.replace(CONTROL_CHARS, "_").replace(/:/g, "_").trim();
  return isDotsAndSpaces(clean) ? "download" : clean;
};

/**
 * A relative path that is safe to write under the current directory. Splits on / and \, drops "." and
 * empty segments, and refuses a ".." segment, a leading separator or a drive letter. A refused or empty
 * path falls back to the bare, sanitized file name.
 */
export const safeRelativePath = (
  candidate: string,
  fileName: string
): string => {
  if (/^[\\/]/.test(candidate)) {
    return bareName(fileName);
  }
  const segments = candidate.split(/[\\/]/).filter(s => s !== "" && s !== ".");
  // Validate the filtered segments: "./C:evil" must not slip past a check on the raw input.
  if (
    segments.length === 0 ||
    segments.some(s => s.includes(":") || isDotsAndSpaces(s))
  ) {
    return bareName(fileName);
  }
  return segments.map(s => s.replace(CONTROL_CHARS, "_")).join("/");
};

export const unixDownloadCommand = (
  link: Link,
  relativePath: string,
  origin: string,
  basename: string
): string =>
  link.method === "stream"
    ? `curl -fL --create-dirs -H "Authorization: Bearer $FOUNDRY_TOKEN" -o ${shQuote(relativePath)} ${shQuote(absolute(link, origin, basename))}`
    : `curl -L --create-dirs -o ${shQuote(relativePath)} ${shQuote(link.url)}`;

export const windowsDownloadCommand = (
  link: Link,
  winFilePath: string,
  origin: string,
  basename: string
): string =>
  link.method === "stream"
    ? `Invoke-WebRequest -Headers @{ Authorization = "Bearer $env:FOUNDRY_TOKEN" } -Uri ${psQuote(absolute(link, origin, basename))} -OutFile ${psQuote(winFilePath)}`
    : `Invoke-WebRequest -Uri ${psQuote(link.url)} -OutFile ${psQuote(winFilePath)}`;

/** The PowerShell line that creates the parent folder, or "" for a bare file name. */
export const windowsMkdirCommand = (winFilePath: string): string =>
  winFilePath.includes("\\")
    ? `$dir = Split-Path ${psQuote(winFilePath)}; if ($dir) { [void](New-Item -ItemType Directory -Force -Path $dir) }\n`
    : "";
